import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { notificationsAPI } from '../services/api';
import { Bell, Check, CurrencyDollar, UserPlus, HandCoins, Lightning } from '@phosphor-icons/react';

const ICON_MAP = {
  payment_received: CurrencyDollar,
  payment_request: HandCoins,
  new_follower: UserPlus,
  purchase: Lightning,
};

const COLOR_MAP = {
  payment_received: '#22C55E',
  payment_request: '#F59E0B',
  new_follower: '#22C55E',
  purchase: '#22C55E',
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const { data } = await notificationsAPI.getUnreadCount();
      setUnreadCount(data.count);
    } catch (err) { /* push notification error */ }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const { data } = await notificationsAPI.list(20);
      setNotifications(data);
    } catch (err) { /* push notification error */ }
    setLoading(false);
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) { /* push notification error */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) { /* push notification error */ }
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-[#1A1A1A] transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5 text-[#999999]" weight={unreadCount > 0 ? 'fill' : 'regular'} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-black bg-[#22C55E] rounded-full px-1" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[360px] max-h-[480px] bg-[#111111] border border-[#222222] rounded-xl shadow-2xl overflow-hidden z-50" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1A1A1A]">
            <h3 className="text-sm font-semibold text-white">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-[#22C55E] hover:text-[#1EA34B] font-medium transition-colors"
                data-testid="mark-all-read"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto max-h-[400px]">
            {loading && notifications.length === 0 && (
              <div className="p-6 text-center text-[#666666] text-sm">Loading...</div>
            )}
            {!loading && notifications.length === 0 && (
              <div className="p-8 text-center">
                <Bell className="h-8 w-8 text-[#333333] mx-auto mb-2" />
                <p className="text-sm text-[#666666]">No notifications yet</p>
              </div>
            )}
            {notifications.map((n) => {
              const Icon = ICON_MAP[n.type] || Bell;
              const color = COLOR_MAP[n.type] || '#999999';
              return (
                <div
                  key={n.notification_id}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-[#1A1A1A] transition-colors cursor-pointer hover:bg-[#1A1A1A] ${!n.read ? 'bg-[#0D1F0D]' : ''}`}
                  onClick={() => { if (!n.read) handleMarkRead(n.notification_id); }}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="h-4 w-4" weight="duotone" style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-white leading-tight">{n.title}</p>
                    <p className="text-xs text-[#888888] mt-0.5 leading-snug line-clamp-2">{n.message}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] text-[#555555]">{timeAgo(n.created_at)}</span>
                      {n.link && (
                        <Link
                          to={n.link}
                          onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                          className="text-[10px] text-[#22C55E] hover:text-[#1EA34B]"
                        >
                          View
                        </Link>
                      )}
                    </div>
                  </div>
                  {!n.read && (
                    <div className="shrink-0 w-2 h-2 rounded-full bg-[#22C55E] mt-2" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
